import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

/** One connected agent platform, as the connections screen shows it. Never carries the token hash. */
export interface AgentConnection {
  id: string;
  revision: string;
  agentName?: string;
  agentPlatform?: string;
  /** Absolute https, already validated server-side; safe to render as an href. */
  agentUrl?: string;
  connectedDate?: string;
  lastUsedDate?: string;
  expiresDate?: string;
  /** Threads this connection currently has open — what it is actually doing. */
  openThreadCount?: number;
  /** The connection's primary conversation id, for a "view conversation" link. */
  conversationId?: string;
}

export const agentConnectionKeys = {
  all: ['agent-connections'] as const,
};

/** One identity for "none", so a reader keyed on the array does not recompute every render while loading. */
const NO_CONNECTIONS: AgentConnection[] = [];

export function useAgentConnections(): { connections: AgentConnection[]; loading: boolean; error: boolean } {
  const query = useQuery({
    queryKey: agentConnectionKeys.all,
    queryFn: async (): Promise<AgentConnection[]> => {
      const res = await apiFetch(`${API_BASE}/api/agent-connections`, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = (await res.json()) as { connections?: AgentConnection[] };
      return json.connections ?? [];
    },
  });

  return { connections: query.data ?? NO_CONNECTIONS, loading: query.isLoading, error: query.isError };
}

/**
 * Mint a fresh act-as-me token for pasting into the user's own Claude Code.
 *
 * The token is returned exactly once — it is the secret half of a grant that is only ever stored
 * hashed — so the caller must surface it to the user immediately; it cannot be re-fetched. On success
 * the connections list is invalidated so the new connection appears in it (and can be disconnected).
 */
export function useConnectClaude() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (label?: string): Promise<{ token: string }> => {
      const res = await apiFetch(`${API_BASE}/api/agent-connections/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(label ? { label } : {}),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = (await res.json()) as { access_token?: string };
      if (!json.access_token) {
        throw new Error('no token in response');
      }
      return { token: json.access_token };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentConnectionKeys.all });
    },
  });
}

/**
 * Disconnect, with an optimistic removal and a rollback.
 *
 * Optimistic because the user has just decided to revoke and should see it gone; rolled back on
 * failure because a connection that silently reappears on the next refetch is worse than one that
 * never left — the user would believe they had revoked access they still hold.
 */
export function useDisconnectAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentGrantId: string) => {
      const res = await apiFetch(`${API_BASE}/api/agent-connections/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentGrantId }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as { disconnected: boolean; closedThreads: number };
    },
    onMutate: async (agentGrantId: string) => {
      await queryClient.cancelQueries({ queryKey: agentConnectionKeys.all });
      const previous = queryClient.getQueryData<AgentConnection[]>(agentConnectionKeys.all);
      queryClient.setQueryData<AgentConnection[]>(agentConnectionKeys.all, (current) =>
        (current ?? []).filter((connection) => connection.id !== agentGrantId),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(agentConnectionKeys.all, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: agentConnectionKeys.all });
    },
  });
}
